import React from 'react';
import PropTypes from 'prop-types';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import Empty from './Empty.jsx';
import PanelWithUpsell from './PanelWithUpsell.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import RIQMetricPieChart from '../../../components/RIQMetricPieChart.jsx';
import useContentModal from '../hooks/useContentModal.jsx';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

const {
    title,
    description
} = listeningDefinitions['source-chart'];

SourceWidget.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string
};

const emptyFallback = <Empty subject='sources'/>;

export default function SourceWidget({instanceId, className, pdfExportConfig, appSpecificExportConfig}) {

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const {showContentModal, ContentModal} = useContentModal(instanceId);

    const metricFilters = useListeningCurationFilterValues(instanceId);

    const metricRequests = {
        dimensionValue: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['source'],
            measure: {
                field: 'count',
                returns: `dimensionValue(source)`
            },
            filters: _.defaults({
                listening: {
                    ...metricFilters.listening,
                    limit: -1
                }
            }, metricFilters)
        },
        sourceCount: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['source'],
            measure: 'count',
            filters: _.defaults({
                listening: {
                    ...metricFilters.listening,
                    limit: -1
                }
            }, metricFilters)
        },
        sourceShareOfVoice: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['source'],
            measure: {
                field: 'count',
                transform: 'percentOfTotal'
            },
            filters: _.defaults({
                listening: {
                    ...metricFilters.listening,
                    limit: -1
                }
            }, metricFilters)
        }
    };

    function onClick(item, chartConfig) {
        const metricFilters = {
            source: null
        };

        metricFilters.selectedSource = [item.id];
        showContentModal(metricFilters);
    }

    return (
        <PanelWithUpsell
            instanceId={instanceId}
            title={'Sources'}
            description={description}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            showViewMore={false}
            className={className}
        >
            <MetricLoadingView
                emptyFallback={emptyFallback}
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);
                        const dimensionValueFormatter = RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.dimensionValue, metricResults.dimensionValue, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated);

                        const dataByInterval = [
                            _.map(collatedResults, (result, idx) => {
                                if (_.has(result, 'sourceCount')) {
                                    return {
                                        id: result.dimensionValue[0],
                                        name: dimensionValueFormatter(result.dimensionValue[0]),
                                        value: result.sourceCount[1],
                                        totalPercentage: result.sourceShareOfVoice[1]
                                    };
                                }
                            }).sort(
                                ({value: a}, {value: b}) => b - a
                            )
                        ];

                        const pieColorsSource = RivalIQ.utils.color.donut();
                        const chartConfigs = [{
                            dataKey: 'value',
                            pieColors: pieColorsSource,
                            abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.sourceCount, metricResults.sourceCount, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                            expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.sourceCount, metricResults.sourceCount, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                            percentageFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.sourceShareOfVoice, metricResults.sourceShareOfVoice, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                            metricRequest: metricRequests.sourceCount,
                            innerRadius: 0,
                            outerRadius: '100%',
                            width: '22%'
                        }];

                        return (
                            <RIQMetricPieChart
                                dataByInterval={dataByInterval}
                                chartConfigs={chartConfigs}
                                onClick={onClick}
                                pieChartHeight={318}
                            />
                        );
                    }
                }
            </MetricLoadingView>

            {ContentModal}
        </PanelWithUpsell>
    );
}
